export const DEFAULT_TOOLTIP_GAP = 8;
export const DEFAULT_TOOLTIP_PADDING = 8;
export const TOOLTIP_ARROW_SIZE = 8;

export type TooltipSide = "top" | "bottom";

export interface RectLike {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PlaceAnchoredTooltipInput {
  anchor: RectLike;
  tooltip: { width: number; height: number };
  viewport: { width: number; height: number };
  gap?: number;
  padding?: number;
  prefer?: TooltipSide;
}

export interface PlaceAnchoredTooltipResult {
  top: number;
  left: number;
  side: TooltipSide;
  /** Arrow center X, relative to the tooltip's left edge. */
  arrowLeft: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Place a floating tooltip next to an anchor, flipping vertically when needed
 * and sliding horizontally so the box stays inside the viewport. The arrow
 * stays aimed at the anchor center even after the box is clamped.
 */
export function placeAnchoredTooltip({
  anchor,
  tooltip,
  viewport,
  gap = DEFAULT_TOOLTIP_GAP,
  padding = DEFAULT_TOOLTIP_PADDING,
  prefer = "top",
}: PlaceAnchoredTooltipInput): PlaceAnchoredTooltipResult {
  const maxWidth = Math.max(0, viewport.width - padding * 2);
  const maxHeight = Math.max(0, viewport.height - padding * 2);
  const tw = Math.min(Math.max(tooltip.width, 0), maxWidth);
  const th = Math.min(Math.max(tooltip.height, 0), maxHeight);

  const anchorTop = anchor.y;
  const anchorBottom = anchor.y + anchor.height;
  const anchorCenterX = anchor.x + anchor.width / 2;

  const spaceAbove = anchorTop - padding;
  const spaceBelow = viewport.height - padding - anchorBottom;
  const need = th + gap;

  let side: TooltipSide = prefer;
  if (prefer === "top" && spaceAbove < need && spaceBelow > spaceAbove) {
    side = "bottom";
  } else if (prefer === "bottom" && spaceBelow < need && spaceAbove > spaceBelow) {
    side = "top";
  }

  const rawTop = side === "top" ? anchorTop - gap - th : anchorBottom + gap;
  const top = clamp(rawTop, padding, Math.max(padding, viewport.height - padding - th));

  const rawLeft = anchorCenterX - tw / 2;
  const left = clamp(rawLeft, padding, Math.max(padding, viewport.width - padding - tw));

  const arrowMin = TOOLTIP_ARROW_SIZE;
  const arrowMax = Math.max(arrowMin, tw - TOOLTIP_ARROW_SIZE);
  const arrowLeft = clamp(anchorCenterX - left, arrowMin, arrowMax);

  return { top, left, side, arrowLeft };
}

export function viewportTooltipBounds(
  viewport: { width: number; height: number },
  padding = DEFAULT_TOOLTIP_PADDING,
): { maxWidth: number; maxHeight: number } {
  return {
    maxWidth: Math.max(0, viewport.width - padding * 2),
    maxHeight: Math.max(0, viewport.height - padding * 2),
  };
}
